import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { createTicket } from '../../../store/actions/ticketAction';
import { apiRequest } from '../../../services/apiService';

export default function CreateTicketModal({ show, onClose, onSuccess }) {
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const { creating } = useSelector((state) => state.ticketData);
    const [users, setUsers] = useState([]);

    const M7 = window.modulePermissions?.M7 || {};

    useEffect(() => {
        if (M7.view_all == 1) {
            loadUsers();
        }
    }, []);

    const loadUsers = async () => {
        const { response } = await apiRequest({
            method: 'POST',
            url: '/users/getUserList',
            data: { startingLimit: 0, resultsPerPage: 200, search: '' },
        });
        if (response?.data?.data) {
            setUsers(response.data.data);
        }
    };

    const createTicketSchema = Yup.object().shape({
        title: Yup.string().required(t('tickets.create.title_required')).max(255, t('tickets.create.title_max')),
        severity: Yup.string().required(t('tickets.create.severity_required')).oneOf(['low', 'medium', 'high', 'critical']),
        description: Yup.string().max(2000, t('tickets.create.description_max')),
        assigned_to: Yup.string(),
    });

    const formik = useFormik({
        initialValues: {
            title: '',
            severity: '',
            description: '',
            assigned_to: '',
        },
        validationSchema: createTicketSchema,
        onSubmit: async (values) => {
            const payload = {
                title: values.title,
                severity: values.severity,
                description: values.description || undefined,
                assigned_to: values.assigned_to || undefined,
            };
            const result = await dispatch(createTicket(payload));
            if (result?.success) {
                formik.resetForm();
                onSuccess();
            }
        },
    });

    if (!show) return null;

    return (
        <>
            <div className="modal-backdrop fade show" style={{ zIndex: 1040 }} />
            <div className="modal fade show d-block" tabIndex={-1} style={{ zIndex: 1050 }}
                onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
                <div className="modal-dialog modal-dialog-centered">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h5 className="modal-title" style={{ fontWeight: 700, color: '#0F2744' }}>{t('tickets.create.title')}</h5>
                            <button type="button" className="btn-close" onClick={onClose} />
                        </div>
                        <form onSubmit={formik.handleSubmit}>
                            <div className="modal-body">
                                <div className="mb-3">
                                    <label className="form-label" style={{ fontSize: 13, fontWeight: 600 }}>{t('tickets.create.title_label')}</label>
                                    <input
                                        type="text"
                                        className={`form-control form-control-sm ${formik.touched.title && formik.errors.title ? 'is-invalid' : ''}`}
                                        name="title"
                                        value={formik.values.title}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        placeholder={t('tickets.create.title_placeholder')}
                                    />
                                    {formik.touched.title && formik.errors.title && (
                                        <div className="invalid-feedback">{formik.errors.title}</div>
                                    )}
                                </div>

                                <div className="mb-3">
                                    <label className="form-label" style={{ fontSize: 13, fontWeight: 600 }}>{t('tickets.create.severity_label')}</label>
                                    <select
                                        className={`form-select form-select-sm ${formik.touched.severity && formik.errors.severity ? 'is-invalid' : ''}`}
                                        name="severity"
                                        value={formik.values.severity}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}>
                                        <option value="">{t('tickets.create.severity_placeholder')}</option>
                                        <option value="low">{t('tickets.severity.low')}</option>
                                        <option value="medium">{t('tickets.severity.medium')}</option>
                                        <option value="high">{t('tickets.severity.high')}</option>
                                        <option value="critical">{t('tickets.severity.critical')}</option>
                                    </select>
                                    {formik.touched.severity && formik.errors.severity && (
                                        <div className="invalid-feedback">{formik.errors.severity}</div>
                                    )}
                                </div>

                                <div className="mb-3">
                                    <label className="form-label" style={{ fontSize: 13, fontWeight: 600 }}>{t('tickets.create.description_label')}</label>
                                    <textarea
                                        className="form-control form-control-sm"
                                        name="description"
                                        rows={4}
                                        value={formik.values.description}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        placeholder={t('tickets.create.description_placeholder')}
                                        style={{ resize: 'vertical' }}
                                    />
                                    {formik.touched.description && formik.errors.description && (
                                        <div className="invalid-feedback d-block">{formik.errors.description}</div>
                                    )}
                                </div>

                                {M7.view_all == 1 && users.length > 0 && (
                                    <div className="mb-3">
                                        <label className="form-label" style={{ fontSize: 13, fontWeight: 600 }}>{t('tickets.create.assign_to')}</label>
                                        <select
                                            className="form-select form-select-sm"
                                            name="assigned_to"
                                            value={formik.values.assigned_to}
                                            onChange={formik.handleChange}>
                                            <option value="">{t('tickets.create.unassigned')}</option>
                                            {users.map((u) => (
                                                <option key={u.id} value={u.id}>{u.name} ({u.email})</option>
                                            ))}
                                        </select>
                                    </div>
                                )}
                            </div>
                            <div className="modal-footer">
                                <button type="button" className="btn btn-sm btn-outline-secondary" onClick={onClose}
                                    disabled={creating}>
                                    {t('tickets.create.cancel')}
                                </button>
                                <button type="submit" className="btn btn-sm btn-danger" disabled={creating}>
                                    {creating ? t('tickets.create.creating') : t('tickets.create.submit')}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </>
    );
}
